import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { Capacitor } from "@capacitor/core";
import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  deleteToken,
  onMessage,
  isSupported as isMessagingSupported,
} from "firebase/messaging";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { FirebasePerformance } from "@capacitor-firebase/performance";
import { FirebaseCrashlytics } from "@capacitor-firebase/crashlytics";
import { Device } from "@capacitor/device";
import { storeToRefs } from "pinia";
import { f7 } from "framework7-vue";

import {
  FIREBASE_CONFIG,
  FCM_VAPID_KEY,
  isFirebaseConfigComplete,
  warnOnMissingFirebaseConfig,
} from "../firebase/config.js";
import { useAuthStore } from "../pinia-stores/auth.js";
import { useKeycloakStore } from "../pinia-stores/keycloak.js";
import { useAccountStore } from "../pinia-stores/account.js";
import { useLocationStore } from "../pinia-stores/location.js";
import { useNotificationStore } from "../pinia-stores/notification.js";

const NOTIFICATION_ICON = "/avocash_notification_icon.png";
const IN_APP_ICON_HTML =
  '<i class="icon elevation-3"><img src="/512x512.svg" class="avocash_logo" width="32" height="32" alt="avocash icon"></i>';

let firebaseApp = null;
let messaging = null;
let nativeMessaging = null;
let webSwRegistration = null;
let initializationPromise = null;
let lastRegisteredMarker = null;
let lastRegisteredDeviceToken = "";
const activeListeners = [];

const firebaseAppRef = ref(null);
const messagingRef = ref(null);
const analyticsRef = ref(null);
const performanceRef = ref(null);
const deviceToken = ref("");
const permission = ref("default");
const initialized = ref(false);

/**
 * useFirebase - Unified Firebase client for web (PWA) and Capacitor native.
 *
 * Responsibilities:
 *  - Web + native FCM token lifecycle, tied to the Keycloak session.
 *  - Foreground notifications (in-app) and background/system notifications.
 *  - Native Analytics, Performance and Crashlytics bootstrap (with JS error capture).
 *  - Notification permission handling (first-time opt-in after login only).
 *
 * Safe to call from multiple components: initialization is idempotent and shared.
 */
export function useFirebase() {
  const authStore = useAuthStore();
  const keycloakStore = useKeycloakStore();
  const accountStore = useAccountStore();
  const locationStore = useLocationStore();
  const notificationStore = useNotificationStore();

  const { authenticated } = storeToRefs(authStore);
  const { profile } = storeToRefs(authStore);
  const { userAccount } = storeToRefs(accountStore);
  const { userAddress } = storeToRefs(locationStore);

  const prevAuthenticated = ref(null);

  async function getNativeMessaging() {
    if (nativeMessaging) return nativeMessaging;
    if (!Capacitor.isPluginAvailable("FirebaseMessaging")) {
      console.warn("[Firebase] FirebaseMessaging plugin not available on this platform");
      nativeMessaging = null;
      return null;
    }
    try {
      const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
      nativeMessaging = FirebaseMessaging || null;
    } catch (error) {
      console.warn("[Firebase] Unable to import FirebaseMessaging:", error);
      nativeMessaging = null;
    }
    return nativeMessaging;
  }

  function waitForAuthToken(timeoutMs = 10000) {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const tick = () => {
        if (keycloakStore.token || Date.now() - startedAt >= timeoutMs) {
          resolve(keycloakStore.token);
          return;
        }
        setTimeout(tick, 250);
      };
      tick();
    });
  }

  async function readNotificationPermission() {
    if (Capacitor.isNativePlatform()) {
      try {
        const FirebaseMessaging = await getNativeMessaging();
        if (!FirebaseMessaging) return "default";
        const { receive } = await FirebaseMessaging.checkPermissions();
        if (receive === "granted") return "granted";
        if (receive === "denied") return "denied";
        return "default";
      } catch (_) {
        return "default";
      }
    }
    if (!("Notification" in window)) return "denied";
    return window.Notification.permission;
  }

  async function requestNotificationPermission() {
    let status;
    if (Capacitor.isNativePlatform()) {
      try {
        const FirebaseMessaging = await getNativeMessaging();
        if (!FirebaseMessaging) return "default";
        status = (await FirebaseMessaging.requestPermissions()).receive;
      } catch (_) {
        status = "denied";
      }
    } else {
      if (!("Notification" in window)) return "denied";
      status = await window.Notification.requestPermission();
    }
    const normalized =
      status === "granted" || status === "denied" ? status : "default";
    permission.value = normalized;
    return normalized;
  }

  function waitForActiveRegistration(registration) {
    return new Promise((resolve) => {
      if (registration.active) {
        resolve(registration);
        return;
      }
      const worker = registration.installing || registration.waiting;
      if (worker) {
        worker.addEventListener("statechange", () => {
          if (worker.state === "activated") resolve(registration);
        });
        setTimeout(() => resolve(registration), 5000);
        return;
      }
      setTimeout(() => resolve(registration), 2000);
    });
  }

  async function getWebServiceWorkerRegistration() {
    if (webSwRegistration) return webSwRegistration;
    if (!("serviceWorker" in navigator)) return null;

    if (import.meta.env.PROD) {
      try {
        webSwRegistration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((resolve) => {
            setTimeout(() => {
              navigator.serviceWorker.getRegistration().then(resolve);
            }, 8000);
          }),
        ]);
      } catch (error) {
        console.warn("[Firebase] Service worker ready warning:", error);
      }
      if (!webSwRegistration) {
        webSwRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
          { scope: "/" }
        );
      }
    } else {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
        { scope: "/" }
      );
      webSwRegistration = await waitForActiveRegistration(registration);
    }
    return webSwRegistration;
  }

  function setupForegroundMessageHandler(messagingInstance) {
    const unsubscribe = onMessage(messagingInstance, (payload) => {
      handleMessage(payload);
    });
    activeListeners.push({ remove: unsubscribe });
  }

  async function initializeWebMessaging() {
    if (messagingRef.value) {
      console.log("[Firebase] Web messaging already initialized");
      return messagingRef.value;
    }
    
    if (!isMessagingSupported()) {
      console.warn("[Firebase] Messaging not supported in this environment");
      return null;
    }

    try {
      console.log("[Firebase] Initializing web messaging...");
      firebaseApp = initializeApp(FIREBASE_CONFIG);
      firebaseAppRef.value = firebaseApp;
      messaging = getMessaging(firebaseApp);
      messagingRef.value = messaging;

      setupForegroundMessageHandler(messaging);

      try {
        const registration = await getWebServiceWorkerRegistration();
        if (registration) {
          webSwRegistration = registration;
          console.log("[Firebase] Service worker registered successfully");
        }
      } catch (error) {
        console.warn("[Firebase] Service worker registration warning:", error);
      }
      
      console.log("[Firebase] Web messaging initialized successfully");
      return messaging;
    } catch (error) {
      console.error("[Firebase] Failed to initialize web messaging:", error);
      throw error;
    }
  }

  async function setupNativeListeners() {
    const FirebaseMessaging = await getNativeMessaging();
    if (!FirebaseMessaging) return;

    const tokenListener = await FirebaseMessaging.addListener(
      "tokenReceived",
      (event) => {
        if (event.token && event.token !== deviceToken.value) {
          deviceToken.value = event.token;
          if (authenticated.value) registerToken(event.token);
        }
      }
    );

    const messageListener = await FirebaseMessaging.addListener(
      "notificationReceived",
      (event) => {
        handleMessage(event);
      }
    );

    const actionListener = await FirebaseMessaging.addListener(
      "notificationActionPerformed",
      (event) => {
        handleMessage(event);
        openNotificationUrl(getMessageUrl(event));
      }
    );

    activeListeners.push(tokenListener, messageListener, actionListener);
  }

  async function initializeNativeMessaging() {
    const FirebaseMessaging = await getNativeMessaging();
    if (!FirebaseMessaging) return FirebaseMessaging;
    await setupNativeListeners();

    try {
      await FirebaseMessaging.createChannel({
        id: "avocash_channel",
        name: "avo¢ash",
        description: "avo¢ash notifications",
        importance: 4,
        visibility: 0,
        sound: "coin_sms.wav",
        vibration: true,
        lights: true,
      });
    } catch (_) {}
    return FirebaseMessaging;
  }

  function installCrashlyticsErrorCapture() {
    if (activeListeners.some((l) => l.isErrorCapture)) return;
    const onError = (event) => {
      const stack = event.error ? event.error.stack : "";
      const message = event.message || "Uncaught error";
      try {
        FirebaseCrashlytics.recordException({
          message: stack ? `${message}\n${stack}` : message,
        });
      } catch (_) {}
    };
    const onRejection = (event) => {
      const reason = event.reason || {};
      const stack = reason.stack || "";
      const message =
        (reason && reason.message) || `Unhandled promise rejection: ${String(reason)}`;
      try {
        FirebaseCrashlytics.recordException({
          message: stack ? `${message}\n${stack}` : message,
        });
      } catch (_) {}
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    activeListeners.push(
      { remove: () => window.removeEventListener("error", onError), isErrorCapture: true },
      { remove: () => window.removeEventListener("unhandledrejection", onRejection), isErrorCapture: true }
    );
  }

  function getCurrentUserId() {
    const userId = (
      userAccount.value?.userId ||
      profile.value?.sub ||
      keycloakStore.subject ||
      ""
    );
    console.log("[Firebase] getCurrentUserId - userAccount.userId:", userAccount.value?.userId, 
                "profile.sub:", profile.value?.sub, 
                "keycloakStore.subject:", keycloakStore.subject, 
                "final userId:", userId);
    return userId;
  }

  async function initializeNativeTelemetry() {
    try {
      analyticsRef.value = FirebaseAnalytics;
      performanceRef.value = FirebasePerformance;
      const userId = getCurrentUserId();
      if (userId) {
        await FirebaseAnalytics.setUserId({ userId });
        await FirebaseCrashlytics.setUserId({ userId });
      }
      installCrashlyticsErrorCapture();
    } catch (error) {
      console.warn("[Firebase] Native telemetry init warning:", error);
    }
  }

  async function obtainToken() {
    try {
      if (Capacitor.isNativePlatform()) {
        const FirebaseMessaging = await getNativeMessaging();
        if (!FirebaseMessaging) {
          console.warn("[Firebase] Native messaging not available");
          return "";
        }
        const result = await FirebaseMessaging.getToken();
        console.log("[Firebase] Native token obtained");
        return result.token || "";
      }
      
      if (!messaging) {
        console.warn("[Firebase] Messaging not initialized");
        return "";
      }
      
      const registration = webSwRegistration || (await getWebServiceWorkerRegistration());
      if (!registration) {
        console.warn("[Firebase] Service worker registration not available");
        return "";
      }
      
      const token = await getToken(messaging, {
        vapidKey: FCM_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      
      console.log("[Firebase] Web token obtained");
      return token || "";
    } catch (error) {
      console.error("[Firebase] Error obtaining token:", error);
      throw error;
    }
  }

  async function registerToken(token) {
    if (!authenticated.value) {
      console.warn("[Firebase] Cannot register token - user not authenticated");
      return;
    }
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn("[Firebase] Cannot register token - no user ID available");
      return;
    }

    const marker = `${token}:${userId}`;
    if (lastRegisteredMarker === marker) {
      console.debug("[Firebase] Token already registered for this user, skipping");
      return;
    }

    await waitForAuthToken();

    try {
      const deviceInfo = await Device.getInfo();
      const deviceId = await Device.getId();
      const payload = {
        userId,
        userAgent: (typeof navigator !== "undefined" && navigator.userAgent) || "",
        appId: firebaseApp?.options?.appId || FIREBASE_CONFIG.appId || "",
        token,
        deviceName: deviceInfo.manufacturer
          ? `${deviceInfo.manufacturer} ${deviceInfo.model}`
          : deviceInfo.model || "",
        deviceId: deviceId.identifier || "",
        ipAddress: userAddress.value?.ip || "",
        registerTime: Date.now(),
      };
      
      console.log("[Firebase] Registering device token - Full payload:", JSON.stringify(payload, null, 2));
      console.log("[Firebase] Token value:", token);
      console.log("[Firebase] DeviceId:", deviceId.identifier);
      console.log("[Firebase] User ID being sent:", userId);
      
      await notificationStore.registerDeviceToken(payload);
      
      lastRegisteredDeviceToken = token;
      lastRegisteredMarker = marker;
      deviceToken.value = token;
      
      console.log("[Firebase] Device token registered successfully");
    } catch (error) {
      console.error("[Firebase] Token registration error:", error);
      console.error("[Firebase] Error response:", error.response?.data);
      throw error;
    }
  }

  async function ensureRegisteredToken() {
    const status = await readNotificationPermission();
    permission.value = status;
    
    if (status === "denied") {
      console.warn("[Firebase] Notification permission denied");
      return;
    }
    
    if (status === "default") {
      const requestedStatus = await requestNotificationPermission();
      if (requestedStatus !== "granted") {
        console.warn("[Firebase] Notification permission not granted");
        return;
      }
    }
    
    try {
      console.log("[Firebase] Obtaining FCM token...");
      const token = await obtainToken();
      if (token) {
        console.log("[Firebase] FCM token obtained successfully");
        await registerToken(token);
      } else {
        console.warn("[Firebase] Failed to obtain FCM token");
      }
    } catch (error) {
      console.error("[Firebase] Token retrieval error:", error);
      throw error;
    }
  }

  async function revokeDeviceSubscription(deleteNativeToken = true) {
    const tokenToRemove = lastRegisteredDeviceToken || deviceToken.value;
    lastRegisteredMarker = null;
    lastRegisteredDeviceToken = "";
    deviceToken.value = "";

    if (tokenToRemove) {
      try {
        await notificationStore.unregisterDeviceToken(tokenToRemove);
      } catch (_) {}
    }

    if (deleteNativeToken) {
      try {
        if (Capacitor.isNativePlatform()) {
          const FirebaseMessaging = await getNativeMessaging();
          if (FirebaseMessaging) await FirebaseMessaging.deleteToken();
        } else if (messaging) {
          await deleteToken(messaging);
        }
      } catch (_) {}
    }
  }

  async function syncSubscription() {
    const wasAuthenticated = prevAuthenticated.value;
    prevAuthenticated.value = authenticated.value;

    if (!authenticated.value) {
      // Only revoke server mapping on a real logout transition, not on initial load
      if (wasAuthenticated === true) {
        await revokeDeviceSubscription(false);
      }
      return;
    }
    await ensureRegisteredToken();
  }

  function getMessageUrl(event) {
    const data = event.data || {};
    const notification = event.notification || {};
    return (
      data.clickAction ||
      data.link ||
      data.url ||
      notification.clickAction ||
      notification.link ||
      "/"
    );
  }

  function normalizeMessage(event) {
    const notification = event.notification || {};
    const data = event.data || {};
    const title = notification.title || data.title || "";
    const body = notification.body || data.body || "";
    const image = notification.image || data.image || "";
    const id =
      data.id ||
      data.notificationId ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    return {
      ...data,
      id,
      title,
      body,
      image,
      url: getMessageUrl(event),
      type: data.type || "push",
    };
  }

  function openNotificationUrl(url) {
    if (!url || url === "/") return;
    try {
      const view = f7 && f7.views && f7.views.main;
      if (view && view.router) {
        view.router.navigate(url);
      } else {
        window.location.assign(url);
      }
    } catch (_) {}
  }

  function showInAppNotification(message) {
    console.log("showInAppNotification", message);
    if (!message.title && !message.body) return;
    try {
      f7.notification
        .create({
          title: message.title || "avo¢ash",
          text: message.body || "",
          icon: IN_APP_ICON_HTML,
          on: {
            click: () => openNotificationUrl(message.url),
          },
        })
        .open();
    } catch (_) {}
  }

  async function showWebNotification(message) {
        console.log("showWebNotification", message);

    if (!message.title && !message.body) return;
    try {
      if (!("serviceWorker" in navigator)) return;
      const registration = navigator.serviceWorker.getRegistration
        ? await navigator.serviceWorker.getRegistration()
        : null;
      if (!registration || !registration.showNotification) return;
      const options = {
        body: message.body || "",
        icon: NOTIFICATION_ICON,
        data: { url: message.url || "/" },
      };
      if (message.image) options.image = message.image;
      await registration.showNotification(message.title || "avo¢ash", options);
    } catch (error) {
      console.warn("[Firebase] Notification display error:", error);
    }
  }

  function handleMessage(event) {
    const message = normalizeMessage(event);
    notificationStore.addNewNotification(message);

    if (Capacitor.isNativePlatform()) {
      if (!event.notification && (message.title || message.body)) {
        showInAppNotification(message);
      }
      return;
    }

    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      showInAppNotification(message);
    } else {
      showWebNotification(message);
    }
  }

  async function subscribeToTopic(topicId) {
    try {
      if (Capacitor.isNativePlatform() && nativeMessaging) {
        await nativeMessaging.subscribeToTopic({ topic: topicId });
      }
      await notificationStore.subscribeToTopic(topicId);
    } catch (error) {
      console.error("[Firebase] Subscribe topic error:", error);
    }
  }

  async function unsubscribeFromTopic(topicId) {
    try {
      if (Capacitor.isNativePlatform() && nativeMessaging) {
        await nativeMessaging.unsubscribeFromTopic({ topic: topicId });
      }
    } catch (error) {
      console.error("[Firebase] Unsubscribe topic error:", error);
    }
  }

  async function unsubscribeFromAll() {
    for (const listener of activeListeners.splice(0)) {
      try {
        if (typeof listener.remove === "function") await listener.remove();
        else if (listener.remove) await listener.remove();
      } catch (_) {}
    }
  }

  async function init() {
    if (initialized.value) {
      console.log("[Firebase] Already initialized");
      return initializationPromise;
    }
    
    initializationPromise = (async () => {
      try {
        console.log("[Firebase] Starting initialization...");
        
        if (Capacitor.isNativePlatform()) {
          console.log("[Firebase] Native platform detected");
          await initializeNativeMessaging();
          await initializeNativeTelemetry();
          // Don't sync subscription here - wait for authentication
          console.log("[Firebase] Native messaging initialized, waiting for authentication");
        } else {
          console.log("[Firebase] Web platform detected");
          if (!isFirebaseConfigComplete()) {
            warnOnMissingFirebaseConfig();
            console.error("[Firebase] Cannot initialize - incomplete Firebase configuration");
            return;
          }
          await initializeWebMessaging();
          // Don't sync subscription here - wait for authentication
          console.log("[Firebase] Web messaging initialized, waiting for authentication");
        }
        
        console.log("[Firebase] Initialization completed successfully");
      } catch (error) {
        console.error("[Firebase] Initialization error:", error);
        throw error;
      } finally {
        initialized.value = true;
      }
    })();
    return initializationPromise;
  }

  watch(
    authenticated,
    async (isAuthenticated) => {
      console.log("[Firebase] Auth state changed - authenticated:", isAuthenticated, "initialized:", initialized.value);
      try {
        if (initialized.value && isAuthenticated) {
          console.log("[Firebase] User authenticated, syncing subscription...");
          await syncSubscription();
        } else if (!isAuthenticated) {
          console.log("[Firebase] User logged out, revoking subscription...");
          await syncSubscription();
        }
      } catch (error) {
        console.error("[Firebase] Auth sync error:", error);
      }
    },
    { deep: false, immediate: true }
  );

  onMounted(() => {
    init();
  });

  onBeforeUnmount(() => {
    unsubscribeFromAll();
  });

  return {
    firebaseApp: firebaseAppRef,
    messaging: messagingRef,
    analytics: analyticsRef,
    performance: performanceRef,
    deviceToken,
    permission,
    initialized,
    init,
    initFirebase: init,
    requestNotificationPermission,
    revokeDeviceSubscription,
    subscribeToTopic,
    unsubscribeFromTopic,
    handleMessage,
    showInAppNotification,
    showWebNotification,
  };
}